import React from 'react';
import {Provider as ReduxProvider} from 'react-redux';
import {ThemeProvider} from 'styled-components';
import {Link as RouterLink, type LinkProps as RouterLinkProps} from 'react-router-dom';

import {Notice} from '@famly/mf_data-display_notice';
import {ModernFamlyProvider, type SupportedLanguage as MFSupportedLanguage} from '@famly/mf_system_provider';
import Application from 'web-app/react/routes/application';
import {getLocale} from 'web-app/react/session/account/selectors';
import store from 'web-app/react/redux/store';
import currentTheme, {getCurrentModernFamlyTheme} from 'web-app/styleguide/themes';
import PlatformHelper from 'web-app/helpers/platform-helper';
import useTypedSelector from 'web-app/react/hooks/use-typed-selector';
import LocalStorageService from 'web-app/services/local-storage';
import {getBrowserLanguage, isSupportedLanguage, getModernFamlyLanguage} from 'web-app/setup/locale-setup';
import {removeLeadingHashtagFromNavigationHashIfNeeded} from 'web-app/util/url';
import {isE2e} from '@famly/mf_ts-utils_is-e2e';
import {Box} from 'famly/mf/layout_box/src/box';

const App: React.FC = () => {
    return (
        <ReduxProvider store={store}>
            <PlatformHelperProvider />
            <ThemedApp />
        </ReduxProvider>
    );
};

const PlatformHelperProvider = () => {
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (ref.current) {
            (ref.current as any).PlatformHelper = PlatformHelper;
        }
    });

    if (!isE2e()) {
        return null;
    }
    return <div ref={ref} data-e2e-id="platform-helper" />;
};

const ThemedApp = () => {
    const [showNotice, setShowNotice] = React.useState(true);
    const theme = React.useMemo(() => {
        return currentTheme().toObject();
    }, []);

    const modernFamlyLanguage = useModernFamlyLanguage();

    const modernFamlyTheme = getCurrentModernFamlyTheme();

    return (
        <ModernFamlyProvider
            theme={modernFamlyTheme}
            isMobileApp={PlatformHelper.isMobileApp()}
            language={modernFamlyLanguage}
            LinkBehavior={LinkBehavior}
        >
            <ThemeProvider theme={theme}>
                <div className="application-view">
                    {theme.title === 'Base' && showNotice && (
                        <Box position="fixed" top={100} width={'50%'} zIndex={1000} ml={'25%'} mr={'auto'}>
                            <Notice
                                severity="warning"
                                text="Using base theme. Please update styleguide/themes/index.ts to use a custom theme."
                                button={{
                                    text: 'close',
                                    onClick: () => setShowNotice(false),
                                }}
                            />
                        </Box>
                    )}
                    <Application />
                </div>
            </ThemeProvider>
        </ModernFamlyProvider>
    );
};

/**
 * Configures Modern Famly to use react-router-dom's <Link /> component in place of native <a /> tags.
 */
const LinkBehavior = React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & {href: RouterLinkProps['to']}>(
    (props, ref) => {
        const {href, target, ...other} = props;

        // If the `href` starts with "#" or "/" we assume that we're routing internally. We want to use `react-router` for this...
        if (href.startsWith('#') || href.startsWith('/')) {
            const safeHref = removeLeadingHashtagFromNavigationHashIfNeeded(href);
            return <RouterLink ref={ref} to={safeHref} target={target} {...other} />;
        }

        // ...if not, we assume that the link is external and we'll just use a normal `a` tag.
        return <a href={href} target={target} {...other} />;
    },
);

const useModernFamlyLanguage = () => {
    // This section is used for localization of Modern Famly.
    const userLocale = useTypedSelector(state => getLocale(state));
    const modernFamlyLanguage = React.useMemo<MFSupportedLanguage | undefined>(() => {
        const locale = userLocale ?? LocalStorageService.getFromLocalStorage('locale') ?? getBrowserLanguage();

        // Swiss dutch (de_CH) isn't part of our SupportedLanguages enum,
        // but it's still possible to chose it in the language picker.
        if (!isSupportedLanguage(locale) && locale !== 'de_CH') {
            return 'enUS';
        }

        return getModernFamlyLanguage(locale);
    }, [userLocale]);

    return modernFamlyLanguage;
};

export default App;
